import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function Login({ setUser }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const [org, setOrg] = useState(null);
  const [pageConfig, setPageConfig] = useState(null);
  const { orgId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const orgData = localStorage.getItem('currentOrg');
    if (orgData) {
      setOrg(JSON.parse(orgData));
    }
    loadPageConfig();
  }, [orgId]);

  const loadPageConfig = async () => {
    try {
      const res = await axios.get(`${API}/page-config/login?organization_id=${orgId || 'system'}`);
      setPageConfig(res.data);
    } catch (error) {
      console.error('Failed to load page config', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('請輸入用戶名稱和密碼');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { 
        username, 
        password,
        organization_id: orgId  // Pass organization_id for validation
      });
      const userData = res.data.user;
      
      // Strict role validation: only students and parents allowed
      if (!['student', 'parent'].includes(userData.role)) {
        localStorage.removeItem('token');
        toast.error(`此帳號是${userData.role === 'coach' ? '教練' : userData.role === 'org_admin' ? '機構管理員' : '管理員'}帳號，請使用對應的登入頁面`);
        return;
      }
      
      // Additional check for parent login type
      if (isParent && userData.role !== 'parent') {
        localStorage.removeItem('token');
        toast.error('此帳戶不是家長帳戶');
        return;
      }
      
      // Student accounts can only access student interface
      if (!isParent && userData.role !== 'student') {
        localStorage.removeItem('token');
        toast.error('此帳戶不是學生帳戶');
        return;
      }

      localStorage.setItem('token', res.data.access_token);
      setUser(userData);
      toast.success('登入成功');
      navigate('/student/home');
    } catch (error) {
      console.error('Login error:', error);
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail === "Account pending approval") {
        toast.error('您的帳戶正在等待審批，請稍後再試', {
          duration: 5000,
          description: '請聯繫管理員加快審批流程'
        });
      } else if (errorDetail === "Account has been rejected") {
        toast.error('您的帳戶申請已被拒絕', {
          duration: 5000,
          description: '如有疑問請聯繫管理員'
        });
      } else if (errorDetail === "Invalid organization for this account") {
        toast.error('此帳戶不屬於此機構');
      } else {
        toast.error('用戶名稱或密碼錯誤');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('${pageConfig?.background_image || org?.background_image || 'https://images.unsplash.com/photo-1534258936925-c58bed479fcb?w=1200'}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/40 to-teal-500/40" />
      </div>

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8">
          {(pageConfig?.logo_url || org?.logo_url) && (
            <div className="text-center mb-6">
              <img src={pageConfig?.logo_url || org?.logo_url} alt="Logo" className="h-16 mx-auto" />
            </div>
          )}

          <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
            {isParent ? '家長登入' : (pageConfig?.title || '學生登入')}
          </h2>
          <p className="text-center text-gray-600 mb-6">
            {pageConfig?.subtitle || org?.name || '歡迎回來'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Input
                data-testid="username-input"
                type="text"
                placeholder="用戶名稱"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12"
              />
            </div>

            <div>
              <Input
                data-testid="password-input"
                type="password"
                placeholder="密碼"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
            </div>

            <Button 
              data-testid="login-btn"
              type="submit" 
              className="w-full h-12 text-lg"
              style={{ backgroundColor: pageConfig?.theme_color || org?.theme_color || '#FF6B35' }}
              disabled={loading}
            >
              {loading ? '登入中...' : '登入'}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              data-testid="toggle-role-btn"
              onClick={() => setIsParent(!isParent)}
              className="text-gray-700 hover:underline font-medium"
            >
              切換至{isParent ? '學生' : '家長'}登入
            </button>
            <button
              data-testid="register-link"
              onClick={() => navigate(`/register/${orgId}`)}
              className="text-gray-700 hover:underline font-medium"
            >
              註冊新帳戶
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600 mb-2">其他登入方式</p>
            <button
              onClick={() => {
                // 將機構編號存到 localStorage 供教練登入使用
                localStorage.setItem('organization_id', orgId);
                navigate('/coach-login');
              }}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center justify-center gap-2 mx-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              教練登入
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}